/**
 * When does a campaign actually run?
 *
 * Lifted out of CampaignCalendar.jsx so it can be run and tested on its own
 * -- the calendar was painting days that had already passed and days a
 * finished campaign would never see again, and nothing could check it
 * without rendering the whole page.
 */
/**
 * A campaign that will never fire again has no place on a schedule.
 *
 * Cancelled was already excluded. Completed was not, so a finished campaign
 * went on painting dots across future days it would never run on, and a
 * paused one looked identical to an active one. The calendar is meant to
 * answer "what is still going to happen", and it was answering "what the
 * cadence would have produced".
 */
const NEVER_RUNS_AGAIN = new Set(['cancelled', 'canceled', 'completed']);

export function getRunDaysInMonth(program, year, month) {
  const { cadence, start_date, end_date, status } = program;
  if (NEVER_RUNS_AGAIN.has(status)) return new Set();

  const startDate = new Date(start_date);
  const endDate   = end_date ? new Date(end_date) : null;

  const firstOfMonth = new Date(year, month, 1);
  const lastOfMonth  = new Date(year, month + 1, 0);

  if (startDate > lastOfMonth) return new Set();
  if (endDate && endDate < firstOfMonth) return new Set();

  const rangeStart = startDate > firstOfMonth ? startDate : firstOfMonth;
  const rangeEnd   = endDate && endDate < lastOfMonth ? endDate : lastOfMonth;

  const days = new Set();

  const iterDay = new Date(rangeStart);
  iterDay.setHours(0, 0, 0, 0);

  switch (cadence.type) {
    case 'daily':
      for (let d = new Date(iterDay); d <= rangeEnd; d.setDate(d.getDate() + 1)) {
        days.add(d.getDate());
      }
      break;

    case 'weekly':
      for (let d = new Date(iterDay); d <= rangeEnd; d.setDate(d.getDate() + 1)) {
        const iso = d.getDay() === 0 ? 7 : d.getDay(); // JS Sun=0 → ISO 7
        if (cadence.days?.includes(iso)) days.add(d.getDate());
      }
      break;

    case 'biweekly': {
      const MS_WEEK = 7 * 24 * 60 * 60 * 1000;
      const startSunday = new Date(startDate);
      startSunday.setHours(0, 0, 0, 0);
      for (let d = new Date(iterDay); d <= rangeEnd; d.setDate(d.getDate() + 1)) {
        const iso = d.getDay() === 0 ? 7 : d.getDay();
        if (cadence.days?.includes(iso)) {
          const weeksSince = Math.floor((d.getTime() - startSunday.getTime()) / MS_WEEK);
          if (weeksSince >= 0 && weeksSince % 2 === 0) days.add(d.getDate());
        }
      }
      break;
    }

    case 'monthly': {
      const dom = cadence.day;
      if (dom >= 1 && dom <= 31) {
        const candidate = new Date(year, month, dom);
        if (candidate >= rangeStart && candidate <= rangeEnd) days.add(dom);
      }
      break;
    }

    case 'custom':
      (cadence.dates || []).forEach(ds => {
        const d = new Date(ds);
        d.setHours(0, 0, 0, 0);
        if (d.getFullYear() === year && d.getMonth() === month) {
          if (d >= rangeStart && d <= rangeEnd) {
            days.add(d.getDate());
          }
        }
      });
      break;

    default:
      break;
  }

  return days;
}

/**
 * Build { dayNumber → [{ program, isPast }, ...] } for the given month.
 *
 * Every occurrence the cadence produces is tagged with whether it has
 * already gone by, so the grid can show what is still coming separately
 * from what already ran. A daily campaign used to paint all 31 days the
 * same, which told you nothing about what was left.
 */
export function buildDayMap(programs, year, month, { includePast = false, today = new Date() } = {}) {
  const map = {};
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  programs.forEach(prog => {
    getRunDaysInMonth(prog, year, month).forEach(day => {
      const isPast = new Date(year, month, day) < startOfToday;
      // A paused campaign will not fire on its next scheduled day; it is
      // listed only when the operator asks to see everything, so the
      // default view is what is genuinely still going to happen.
      const isDormant = prog.status === 'paused';
      if (!includePast && (isPast || isDormant)) return;
      if (!map[day]) map[day] = [];
      map[day].push({ program: prog, isPast, isDormant });
    });
  });
  return map;
}

